import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from "../../../environments/environment";

export interface Paper {
  _id?: string;
  title: string;
  description: string;
  author: string;
  category: string;
  categories?: Array<{ id: string; name: string }>;
  coverUrl?: string;
  fileUrl: string;
  pages: number;
  price: number;
  isFree: boolean;
  featured: boolean;
  downloads: number;
  createdAt?: Date;
  updatedAt?: Date;
  tags: string[];
  learningObjectives: string[];
  publishedDate: Date;
  content?: string;
  summary?: string;
  keywords: string[];
  language: string;
  isbn?: string;
  publisher?: string;
  year: number;
  status?: number; // 1 = activo, 2 = inactivo, 3 = En revision, 4 = Borrador
}

export interface CreatePaperDto {
  title: string;
  description: string;
  author: string;
  category: string;
  categories?: Array<{ id: string; name: string }>;
  coverUrl?: string;
  fileUrl: string;
  pages: number;
  price: number;
  isFree: boolean;
  featured: boolean;
  tags?: string[];
  learningObjectives?: string[];
  publishedDate?: string;
  content?: string;
  summary?: string;
  keywords?: string[];
  language?: string;
  isbn?: string;
  publisher?: string;
  year: number;
  status?: number; // 1 = activo, 2 = inactivo, 3 = En revision, 4 = Borrador
}

export interface UpdatePaperDto extends Partial<CreatePaperDto> {}

@Injectable({
  providedIn: 'root'
})
export class PapersService {
  private apiUrl = `${environment.apiUrl}/papers`;

  constructor(private http: HttpClient) {}

  // Obtener todos los papers
  getPapers(): Observable<Paper[]> {
    return this.http.get<{ data: Paper[] }>(this.apiUrl).pipe(
      map(response => {
        console.log('Raw response from backend:', response);
        console.log('Papers data extracted:', response.data);
        return response.data;
      })
    );
  }

  // Obtener papers por categoría
  getPapersByCategory(category: string): Observable<Paper[]> {
    return this.http.get<{ data: Paper[] }>(`${this.apiUrl}?category=${category}`).pipe(
      map(response => response.data)
    );
  }

  // Buscar papers
  searchPapers(query: string): Observable<Paper[]> {
    return this.http.get<{ data: Paper[] }>(`${this.apiUrl}?search=${query}`).pipe(
      map(response => response.data)
    );
  }

  // Obtener papers destacados
  getFeaturedPapers(): Observable<Paper[]> {
    return this.http.get<{ data: Paper[] }>(`${this.apiUrl}/featured`).pipe(
      map(response => response.data)
    );
  }

  // Obtener un paper por ID
  getPaper(id: string): Observable<Paper> {
    return this.http.get<{ data: Paper }>(`${this.apiUrl}/${id}`).pipe(
      map(response => response.data)
    );
  }

  // Crear un nuevo paper
  createPaper(paperData: CreatePaperDto, pdfFile: File): Observable<Paper> {
    console.log('Service: Creating paper with data:', paperData);
    console.log('Service: coverUrl in paperData:', paperData.coverUrl);
    
    const formData = new FormData();
    
    // Agregar el archivo PDF
    formData.append('pdfFile', pdfFile);
    
    // Agregar los datos del paper
    Object.keys(paperData).forEach(key => {
      const value = paperData[key as keyof CreatePaperDto];
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
        console.log(`Service: Added to FormData - ${key}:`, value);
      }
    });

    console.log('Service: FormData prepared for createPaper');

    return this.http.post<{ data: Paper }>(this.apiUrl, formData).pipe(
      map(response => {
        console.log('Service: Paper created successfully:', response.data);
        return response.data;
      })
    );
  }

  // Actualizar un paper
  updatePaper(id: string, paperData: UpdatePaperDto, pdfFile?: File): Observable<Paper> {
    console.log('Service: Updating paper with data:', paperData);
    console.log('Service: coverUrl in paperData:', paperData.coverUrl);
    
    const formData = new FormData();
    
    // Agregar el archivo PDF si se proporciona
    if (pdfFile) {
      formData.append('pdfFile', pdfFile);
    }
    
    // Agregar los datos del paper
    Object.keys(paperData).forEach(key => {
      const value = paperData[key as keyof UpdatePaperDto];
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
        console.log(`Service: Added to FormData - ${key}:`, value);
      }
    });

    console.log('Service: FormData prepared for updatePaper');

    return this.http.patch<{ data: Paper }>(`${this.apiUrl}/${id}`, formData).pipe(
      map(response => {
        console.log('Service: Paper updated successfully:', response.data);
        return response.data;
      })
    );
  }

  // Eliminar un paper
  deletePaper(id: string): Observable<void> {
    return this.http.delete<{ data: void }>(`${this.apiUrl}/${id}`).pipe(
      map(response => response.data)
    );
  }

  // Descargar un paper
  downloadPaper(id: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/download`, { responseType: 'blob' });
  }

  // Cambiar estado destacado
  toggleFeatured(id: string): Observable<Paper> {
    return this.http.patch<{ data: Paper }>(`${this.apiUrl}/${id}/toggle-featured`, {}).pipe(
      map(response => response.data)
    );
  }

  // Subir imagen de portada
  uploadCover(coverFile: File): Observable<{ coverUrl: string; message: string }> {
    const formData = new FormData();
    formData.append('coverFile', coverFile);
    
    return this.http.post<{ coverUrl: string; message: string }>(`${this.apiUrl}/upload-cover`, formData);
  }

  // Obtener URL del archivo
  getFileUrl(filename: string): string {
    return `${environment.apiUrl}/papers/file/${filename}`;
  }
}
